import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "use-debounce";

import {
  ArrowNarrowDownIcon,
  ArrowNarrowUpIcon,
  BadgeCheckIcon,
  CheckCircleIcon,
  CheckIcon,
  CogIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import Button from "components/lib/Shared/Button";
import DashOutline from "components/lib/Shared/Icons/DashOutline";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { Constants } from "config/constants";
import { useToast } from "hooks/useToast";
import {
  getSubscriptionsAsync,
  updateSubscriptionState,
} from "state/slices/subscription";
import { getCorporate, updateCorporate } from "state/slices/organizations";
import { updateActiveDashboardState } from "state/slices/activeDashboard";
import PaystackPopup from "./PaystackPopup";
import { updateCompanyAsync } from "state/slices/company";
import { getCompanyDashboardAsync } from "state/slices/companyDashboard";
import {
  changeSubscriptionPlanAsync,
  subscribeAsync,
} from "state/slices/customerSubscription";

const SubscriptionsCards = ({ manageSubscriptions = true }) => {
  const cookieRole = Cookies.get("role");
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedSubscriptionIndex, setSelectedSubscriptionIndex] =
    useState(null);

  const { subscriptions, getSubscriptions } = useSelector(
    (state) => state.subscriptionSlice
  );
  const { modalRow } = useSelector((state) => state.modalsSlice);
  const { organization } = useSelector((state) => state.activeDashboardSlice);
  const { organization: corporate } = useSelector(
    (state) => state.organizationsSlice
  );
  const { company } = useSelector((state) => state.companyDashboardSlice);

  const customerSubscription =
    organization?.activeCustomerSubscription ||
    corporate?.activeCustomerSubscription ||
    company?.activeCustomerSubscription;

  const organizationSubscriptionPlan = customerSubscription?.subscription;

  let orgAdmin = null;
  let showOrgAdmin = false;

  if (company?.orgAdmin) {
    orgAdmin = company?.orgAdmin;
    showOrgAdmin = true;
  }
  if (corporate?.orgAdmin) {
    orgAdmin = corporate?.orgAdmin;
    showOrgAdmin = true;
  }
  if (organization?.isOrgAdmin) {
    orgAdmin = organization._id;
    showOrgAdmin = manageSubscriptions;
  }

  const handleViewSubscription = async (subscription) => {
    dispatch(
      updateSubscriptionState({
        selectedSubscription: subscription,
        mode: "view",
      })
    );
  };

  const handleEditSubscription = (subscription) => {
    dispatch(
      updateSubscriptionState({
        selectedSubscription: subscription,
        mode: "edit",
      })
    );
  };

  const handleManageSubscription = (subscription) => {
    dispatch(
      updateSubscriptionState({
        selectedSubscription: subscription,
        mode: "manage",
      })
    );
  };

  const getSubscriptionDescription = (name) => {
    if (name === Constants.SUBSCRIPTION_NAMES.BASIC) {
      return "Essential features for small teams";
    } else if (name === Constants.SUBSCRIPTION_NAMES.PRINT) {
      return "Advanced printing management";
    } else if (name === Constants.SUBSCRIPTION_NAMES.PREMIUM) {
      return "Complete solution for enterprise needs";
    }
    return "No description available";
  };

  const dispatchGetSubscriptions = async () => {
    await dispatch(
      getSubscriptionsAsync({
        limit: rowsPerPage,
        skip: (currentPage - 1) * rowsPerPage,
        search: debouncedSearchTerm,
        order: "asc",
        orgAdmin: showOrgAdmin ? orgAdmin : "",
      })
    );
  };

  useEffect(async () => {
    if (debouncedSearchTerm) setCurrentPage(1);
    await dispatchGetSubscriptions();
  }, [dispatch, currentPage, debouncedSearchTerm, cookieRole]);

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  if (getSubscriptions.status === "loading") {
    return (
      <div className="relative flex flex-col items-center justify-center p-32 text-center">
        <div className="absolute flex flex-col items-center justify-center">
          <h2 className="text-sm font-medium text-gray-500">
            <SpinnerIcon />
            Loading subscriptions...
          </h2>
        </div>
      </div>
    );
  }

  const subscribe = ({
    subscription = null,
    company = null,
    corporate = null,
  }) => {
    const startDate = new Date();
    const nextBillingDate = new Date(startDate);
    nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
    if (!organizationSubscriptionPlan) {
      return dispatch(
        subscribeAsync({
          data: {
            company,
            corporate,
            subscription,
            startDate,
            nextBillingDate,
          },
        })
      );
    } else {
      return dispatch(
        changeSubscriptionPlanAsync({
          data: {
            customerSubscription: customerSubscription._id,
            fromSubscription: customerSubscription.subscription._id,
            toSubscription: subscription,
            changeType: "change",
            effectiveAt: new Date(),
          },
        })
      );
    }
  };

  const subscribeForOrg = async (subscription) => {
    const subscribeRes = await subscribe({
      subscription: subscription._id,
      corporate: organization._id,
    });
    if (subscribeRes?.payload?._id) {
      toast("success", "Subscription updated successfully");
      const corporateRes = await dispatch(
        getCorporate({
          corporateId: organization._id,
          isDashboard: true,
        })
      );
      dispatch(
        updateActiveDashboardState({
          organization: corporateRes.payload.corporate,
        })
      );
      await dispatchGetSubscriptions();
    }
  };

  const subscribeCorporate = async (subscription) => {
    const subscribeRes = await subscribe({
      subscription: subscription._id,
      corporate: corporate._id,
    });
    if (subscribeRes?.payload?._id) {
      toast("success", "Subscription updated successfully");
      dispatch(
        getCorporate({
          corporateId: corporate._id,
          isDashboard: true,
        })
      );
      await dispatchGetSubscriptions();
    }
  };

  const subscribeCompany = async (subscription) => {
    const subscribeRes = await subscribe({
      subscription: subscription._id,
      company: company._id,
    });
    if (subscribeRes?.payload?._id) {
      toast("success", "Subscription updated successfully");
      dispatch(
        getCompanyDashboardAsync({
          companyId: company._id,
        })
      );
      await dispatchGetSubscriptions();
    }
  };

  const handleSubscribe = async (subscription, index) => {
    setSelectedSubscriptionIndex(index);
    setSubscribing(true);
    if (organization?._id) {
      await subscribeForOrg(subscription);
    }
    if (corporate?._id) {
      await subscribeCorporate(subscription);
    }
    if (company?._id) {
      await subscribeCompany(subscription);
    }

    setSubscribing(false);
    setSelectedSubscriptionIndex(null);
  };

  const ManageButtons = (subscription) => {
    return (
      <div className="flex justify-between">
        <Button
          className="bg-white border border-gray-400 text-primary w-full rounded-r-none"
          onClick={() => handleEditSubscription(subscription)}
        >
          <CogIcon className="w-4 h-4" /> Edit
        </Button>
        <Button
          className="bg-primary text-white w-full rounded-l-none"
          onClick={() => handleManageSubscription(subscription)}
        >
          <CogIcon className="w-4 h-4" /> Manage
        </Button>
      </div>
    );
  };

  const PaystackButtons = (subscription, index) => {
    let btnClass = "";
    let btnText = "";
    let btnIcon = null;
    if (!organizationSubscriptionPlan) {
      btnClass = `bg-primary text-white w-full rounded ${
        selectedSubscriptionIndex === index ? "animate-pulse" : ""
      }`;
      btnText = "Subscribe";
      btnIcon = null;
    } else {
      if (
        subscription.name === Constants.SUBSCRIPTION_NAMES.BASIC ||
        organizationSubscriptionPlan?.name ===
          Constants.SUBSCRIPTION_NAMES.PREMIUM
      ) {
        btnClass = "bg-white text-gray-600 border border-gray-300 w-full";
        btnText = "Downgrade";
      } else {
        btnClass = "bg-primary text-white w-full";
        btnText = "Upgrade";
      }
    }

    return (
      <div>
        <PaystackPopup
          selectedPackage={subscription}
          selectedCurrency={subscription.currencies[0]}
          btnDetails={{
            className: btnClass,
            text: btnText,
            icon: btnIcon,
          }}
          handleSubscribe={() => {
            if (subscription.name === organizationSubscriptionPlan?.name)
              return;
            handleSubscribe(subscription, index);
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div className="w-full">
        {subscriptions.totalCount === 0 ? (
          <div className="relative flex flex-col items-center justify-center p-32 text-center">
            <DashOutline />
            <div className="absolute flex flex-col items-center justify-center">
              <h2 className="text-title5">
                There are no subscriptions to display
              </h2>
            </div>
          </div>
        ) : (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subscriptions.items.map((subscription, index) => {
              const isSubscribed =
                subscription.name === organizationSubscriptionPlan?.name;
              return (
                /** Subscription Card */
                <div
                  className={`${
                    isSubscribed && "grid grid-cols-[15] grid-rows-[20]"
                  }`}
                >
                  {isSubscribed && !manageSubscriptions && (
                    <div className="bg-sflPrimary text-white rounded-full col-start-5 col-end-11 row-start-1 row-end-3 z-30 flex justify-center items-center">
                      <h2 className="text-sm px-2">Current Plan</h2>
                    </div>
                  )}
                  <div
                    key={subscription._id}
                    className={`border rounded-xl mb-4 shadow-sm hover:shadow-md transition relative bg-white flex flex-col gap-6 h-full
                  ${
                    isSubscribed &&
                    !manageSubscriptions &&
                    "border-sflPrimary col-start-1 col-end-[16] row-start-2 row-end-[21]"
                  }
                  `}
                  >
                    <div className="px-6 pt-4 pb-2 flex-1 flex flex-col gap-6">
                      <div>
                        <p className="text-primary font-bold pb-1">
                          {subscription.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {getSubscriptionDescription(subscription.name)}
                        </p>
                      </div>
                      <div>
                        {subscription.currencies.length > 0 ? (
                          <>
                            <p className="text-primary pb-1">
                              <span className="text-2xl font-bold">
                                {subscription.currencies[0].type}{" "}
                                {subscription.currencies[0].basePrice}
                              </span>
                              <span className="text-gray-500 text-sm">
                                {" "}
                                /month
                              </span>
                            </p>
                            <p className="text-gray-500 text-sm">
                              Base price per tenant
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-primary">
                              <span className="text-2xl font-bold">KES 0</span>
                              <span className="text-gray-500 text-sm">
                                {" "}
                                /month
                              </span>
                            </p>
                            <p className="text-gray-500 text-sm">
                              Base price per tenant
                            </p>
                          </>
                        )}
                      </div>
                      {/** Features */}
                      <div>
                        {subscription?.features?.length > 0 && (
                          <ul className="list-none py-4 space-y-2">
                            {subscription.features.map((feature) => {
                              return (
                                <li
                                  key={feature._id}
                                  className="text-sm list-none text-gray-600"
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="flex">
                                      <BadgeCheckIcon className="w-4 h-4 text-green-900 font-bold mr-2" />
                                      {feature.description}
                                    </span>
                                  </div>
                                </li>
                              );
                            })}
                            {subscription?.subscribersLimit && (
                              <li
                                key="subscribers-limit"
                                className="text-sm list-none"
                              >
                                <div className="flex justify-between items-center text-gray-600">
                                  <span className="flex">
                                    <BadgeCheckIcon className="w-4 h-4  text-green-900 font-bold mr-2" />
                                    {`Up to ${subscription.subscribersLimit} seats`}
                                  </span>
                                </div>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                      <div className="mt-auto">
                        {!manageSubscriptions &&
                          subscription.name !==
                            organizationSubscriptionPlan?.name &&
                          PaystackButtons(subscription, index)}
                      </div>
                    </div>

                    <div>
                      {manageSubscriptions && ManageButtons(subscription)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default SubscriptionsCards;
